/**
 * 사용자 접근 관리 섹션 컴포넌트
 * 사용자의 접근 권한을 관리하는 섹션 컴포넌트입니다.
 * 현재 이용자 수와 최대 이용자 수를 표시하고 관리합니다.
 */

import { useState } from "react";
import { ContractOperationProductManage } from "src/api/contract/contract-types";
import { Modal } from "src/api/public-types";
import { BaseButton } from "src/components";
import BaseSectionTitle from "src/components/layout/BaseSectionTitle";
import Link from "src/components/PartnerLink";
import { numberToStringWithComma } from "src/utils";
import ProductMemberModal from "../modals/ProductMemberModal";

/**
 * 사용자 접근 관리 섹션 컴포넌트의 props 인터페이스
 */
type Props = {
  productManage: ContractOperationProductManage; // 상품 관리 정보
  setData: (data: ContractOperationProductManage) => void; // 데이터 업데이트 함수
  goToList: string; // 목록 페이지 URL
  isAuthority?: boolean | undefined; // 권한 여부
};

/**
 * 사용자 접근 관리 섹션 컴포넌트
 * @param productManage 상품 관리 정보
 * @param setData 데이터 업데이트 함수
 * @param goToList 목록 페이지 URL
 * @param isAuthority 권한 여부
 */
const UserAccessSection = ({ productManage, setData, goToList, isAuthority }: Props) => {
  const [memberModal, setMemberModal] = useState<Modal>({ isOpen: false }); // 최대 인원 설정 모달 상태

  return (
    <section>
      <BaseSectionTitle title={"이용자 관리"} />
      <div className="contents-contract-user">
        <div className="contents-detail flex-center-center">
          {/* 현재 이용자 정보 */}
          <div className="text-center px60 py30">
            <div className="mb8">
              <span className="font14 text-primary3">현재 이용자</span>
            </div>
            <div className="mb20">
              <span className="font22 mr5">
                <b>{productManage ? numberToStringWithComma(productManage.memberNums) : 0}</b>
              </span>
              <span className="font18">명</span>
            </div>
            <div>
              <Link to={goToList} target="_blank">
                <BaseButton
                  title="이용자 초대"
                  className="color-white icon-invite"
                  // disabled={detailData?.contractManage?.spaceProductType === "TIME_COURT" || false}
                />
              </Link>
            </div>
          </div>

          {/* 구분선 */}
          <div className="contents-slash">
            <span className="font14">/</span>
          </div>

          {/* 최대 이용자 정보 */}
          <div className="text-center px60 py30">
            <div className="mb8">
              <span className="font14 text-primary3">최대 이용자</span>
            </div>
            <div className="mb20">
              <span className="font22 mr5">
                <b>{productManage ? numberToStringWithComma(productManage.memberMaxNums) : 0}</b>
              </span>
              <span className="font18">명</span>
            </div>
            <div>
              {isAuthority && (
                <BaseButton
                  title="최대 인원 설정"
                  className="color-white"
                  onClick={() => setMemberModal({ isOpen: true })}
                  // disabled={detailData?.contractManage?.spaceProductType === "TIME_COURT" || false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 최대 인원 설정 모달 */}
      {memberModal.isOpen && (
        <ProductMemberModal
          currentMembers={Number(productManage.memberNums)}
          onSubmit={(maxMembers: number) => {
            if (maxMembers > 0) {
              const calendarType = productManage.calendarType !== "" ? productManage.calendarType : "NONE";
              const newProductManage = { ...productManage, memberMaxNums: maxMembers, calendarType };
              setData(newProductManage);
              setMemberModal({ isOpen: false });
            }
          }}
          onClose={() => setMemberModal({ isOpen: false })}
        />
      )}
    </section>
  );
};

export default UserAccessSection;
